import { quoteErrorMessage } from "./quote-error";
/** Unsaved orders estimate with the shared engine; saved quotes retain their server snapshot. */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchCustomerPricing, fetchSupplierPricing } from "Redux/middlwares/supplierPricing";
import { createOrderQuote, setOrderPriceList } from "Services/supplier-pricing";
import { PBtn, PSelect } from "./ui";
import QuoteModal from "./quote-modal";
import { QuoteSnapshot, QuoteTotals, OrderPricingSummaryFrame } from "./quote-document";
import { priceOrder } from "../../utils/supplier-pricing-engine";

type Props = { orderId?: number | null; supplierCustomerId?: number | null; business?: any; priceList?: string; onPriceListChange?: (key: string) => void; onQuoteGenerated?: (snapshot: QuoteSnapshot) => void; onPreview?: (snapshot: QuoteSnapshot) => void; draftItems?: any[]; onSnapshotChange?: (snapshot: QuoteSnapshot | null) => void; beforeGenerate?: () => Promise<unknown> };

export default function OrderPricingPanel({ orderId, supplierCustomerId, priceList = "", onPriceListChange, onQuoteGenerated, onPreview, draftItems, onSnapshotChange, beforeGenerate }: Props) {
  const { t } = useTranslation("supplierPricing");
  const dispatch = useAppDispatch();
  const customerPricing = useAppSelector((state) => state.supplierPricing.customerPricing);
  const savedPricing = useAppSelector((state) => state.supplierPricing.saved);
  const pricingStatus = useAppSelector((state) => state.supplierPricing.status);
  const [key, setKey] = useState(priceList);
  const [busy, setBusy] = useState(false);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [customerError, setCustomerError] = useState(false);
  const [quote, setQuote] = useState<QuoteSnapshot | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  useEffect(() => { onSnapshotChange?.(quote); }, [quote, onSnapshotChange]);
  const savedSelection = useRef(priceList);
  savedSelection.current = priceList;
  const request = useRef(0);
  const pending = useRef(false);
  const customer = supplierCustomerId ? customerPricing[supplierCustomerId] : null;
  const hasCustom = !customerLoading && !customerError && customer?.mode === "custom";

  const isDraft = !orderId && draftItems !== undefined;
  const estimateKey = priceList || "default";
  const estimate = useMemo(() => {
    if (!isDraft || !draftItems) return null;
    if (pricingStatus === "error") return { error: "order.estimateLoadFailed" };
    if (!savedPricing || pricingStatus !== "ready" || (estimateKey !== "default" && customerLoading)) return { loading: true };
    const selectedCustomer = estimateKey === "default" ? null : customer;
    if (estimateKey !== "default" && (estimateKey !== `customer:${supplierCustomerId}` || customerError || !hasCustom)) return { error: "order.estimateLoadFailed" };
    if (!draftItems.length) return { empty: true };
    if (draftItems.some(item => !Number.isFinite(Number(item.quantity)) || Number(item.quantity) <= 0 || !Number.isFinite(Number(item.tool?.unit_weight)) || Number(item.tool?.unit_weight) <= 0)) return { error: "order.estimateItemsIncomplete" };
    const result = priceOrder(draftItems, savedPricing, selectedCustomer, supplierCustomerId || null);
    const { missingPrices, ...totals } = result.totals;
    if (missingPrices.length) return { error: "order.missingPrices", count: missingPrices.length };
    return { data: { totals: { ...totals, weight_ton: totals.weightTon, vat_percent: totals.vatPercent }, customer: { discount_percent: Number(selectedCustomer?.discountPercent || 0).toFixed(2) } } };
  }, [isDraft, estimateKey, draftItems, savedPricing, pricingStatus, customer, customerLoading, customerError, hasCustom, supplierCustomerId]);

  useEffect(() => { setQuote(null); setQuoteOpen(false); }, [savedPricing?.lastModified, customer?.lastModified]);
  useEffect(() => { dispatch(fetchSupplierPricing()); }, [dispatch]);
  useEffect(() => {
    let active = true;
    request.current += 1;
    pending.current = false;
    setKey(savedSelection.current); setQuote(null); setQuoteOpen(false); setBusy(false);
    setCustomerError(false); setCustomerLoading(Boolean(supplierCustomerId));
    if (supplierCustomerId) {
      Promise.resolve(dispatch(fetchCustomerPricing(supplierCustomerId)))
        .catch(() => { if (active) setCustomerError(true); })
        .finally(() => { if (active) setCustomerLoading(false); });
    }
    return () => { active = false; request.current += 1; };
  }, [orderId, supplierCustomerId, dispatch]);

  useEffect(() => { setKey(priceList); }, [priceList]);

  const choose = async (next: string) => {
    if (!next || pending.current) return;
    if (!orderId) {
      setKey(next); setQuote(null); setQuoteOpen(false);
      onPriceListChange?.(next);
      return;
    }
    pending.current = true;
    const token = ++request.current;
    setBusy(true); setQuote(null); setQuoteOpen(false);
    try {
      await setOrderPriceList(orderId, next);
      if (token === request.current) { setKey(next); onPriceListChange?.(next); }
    } catch {
      if (token === request.current) { setKey(""); toast.error(t("order.selectionFailed")); }
    } finally {
      if (token === request.current) { pending.current = false; setBusy(false); }
    }
  };

  const generate = async () => {
    if (!orderId || !key || pending.current) return;
    pending.current = true;
    const token = ++request.current;
    setBusy(true); setQuote(null); setQuoteOpen(false);
    try {
      if (beforeGenerate) {
        try { await beforeGenerate(); }
        catch {
          if (token === request.current) toast.error(t("order.saveBeforeQuoteFailed"));
          return;
        }
        if (token !== request.current) return;
      }
      const response = await createOrderQuote(orderId, key);
      const snapshot = response.data.snapshot as QuoteSnapshot;
      if (!snapshot || snapshot.price_list !== key || snapshot.order.id !== orderId) {
        throw new Error("Quote does not match the explicit selection");
      }
      if (token === request.current) {
        if (onQuoteGenerated) onQuoteGenerated(snapshot);
        else { setQuote(snapshot); if (onPreview) onPreview(snapshot); else setQuoteOpen(true); }
      }
    } catch (error) {
      if (token === request.current) toast.error(quoteErrorMessage(error, t));
    } finally {
      if (token === request.current) { pending.current = false; setBusy(false); }
    }
  };

  return <div className="eb-pr eb-pr-pricing-panel">
    <div className="eb-pr-side">
      <label className="eb-pr-label">{t("order.priceList")}</label>
      <PSelect value={isDraft ? estimateKey : key} onChange={choose} disabled={busy}
        ariaLabel={t("order.priceList")} options={[
          ...(isDraft ? [] : [{ value: "", label: t("order.chooseList") }]),
          { value: "default", label: t("tabs.default") },
          ...(hasCustom ? [{ value: `customer:${supplierCustomerId}`, label: `${t("order.customerList")} — ${customer.customerName || supplierCustomerId}` }] : []),
        ]} />
      {customerLoading && <p role="status">{t("common.loading")}</p>}
      {!customerLoading && <p>{t(isDraft ? "order.livePricingHelp" : customerError ? "order.customerLoadFailed" : hasCustom ? "order.explicitSelection" : "order.noCustomList")}</p>}
      {!orderId && <p>{t("order.saveToApplyServer")}</p>}
      <PBtn kind="quote" disabled={!orderId || !key || busy} onClick={generate}>
        {busy ? t("common.loading") : t("order.createQuote")}
      </PBtn>
    </div>
    {isDraft && <section className="eb-pr-summary" aria-label={t("order.summary")} data-live-pricing-estimate>
      {estimate?.data ? <><QuoteTotals snapshot={estimate.data} presentation="order" /><small>{t("order.liveEstimate")}</small></> :
        <OrderPricingSummaryFrame><div className="eb-pr-summary__row" role={estimate?.error ? "alert" : "status"}>
          {t(estimate?.error || (estimate?.empty ? "order.addItemsForEstimate" : "common.loading"), { count: estimate?.count })}
        </div></OrderPricingSummaryFrame>}
    </section>}
    {quote && <section className="eb-pr-summary" aria-label={t("order.summary")}>
      <QuoteTotals snapshot={quote} presentation="order" />
      <PBtn kind="secondary" onClick={() => onPreview ? onPreview(quote) : setQuoteOpen(true)}>{t("quote.preview")}</PBtn>
    </section>}
    {quote && !onPreview && <QuoteModal show={quoteOpen} onHide={() => setQuoteOpen(false)} snapshot={quote} />}
  </div>;
}
